import ChevronDown from "../icons/ChevronDown";
import { Popover, PopoverContent, PopoverTrigger } from "../components/shadcn/popover";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  Cog8ToothIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { ArrowUpIcon, ArrowDownIcon } from "@heroicons/react/20/solid";
import { renderFieldIcon } from "../fields/FieldList";

interface HeadCellWrapperProps {
  children: React.ReactNode;
  headerData: any;
  iconChevronDown?: boolean;
  showDropdown?: boolean;
  showSorting?: boolean;
  sortDirection?: string;
  currentSortDirection?: string | null;
  onSelectColumn?: () => void;
  onHideColumn?: () => void;
  onEditColumn?: () => void;
  onSortField?: () => void;
  onMoveLeft?: () => void;
  onMoveRight?: () => void;
  isSettings?: boolean;
  isPinned?: boolean;
  isCustomField?: boolean;
  headerIndex?: number;
  headerCount?: number;
}

const HeadCellWrapper: React.FC<HeadCellWrapperProps> = ({
  children,
  headerData,
  iconChevronDown = false,
  showDropdown = true,
  showSorting = true,
  sortDirection = "ASC",
  currentSortDirection = null,
  onSelectColumn,
  onHideColumn,
  onEditColumn,
  onSortField,
  onMoveLeft,
  onMoveRight,
  isSettings = true,
  isPinned: _isPinned = false,
  isCustomField,
  headerIndex = 0,
  headerCount = 0,
}) => {
  const renderFieldIconComponent = () => {
    if (!headerData || (!headerData.id && !headerData.selector)) {
      return null;
    }
    const fieldData = {
      id: headerData.id || headerData.selector,
      type: headerData.selector,
      formType: headerData.formType,
    };
    return (
      <div className="shrink-0 mr-2 text-gray-400 group-hover:text-gray-700 transition-colors">
        {renderFieldIcon(fieldData, "w-[14px] h-[14px]")}
      </div>
    );
  };

  // Sort indicator — shows current sort direction on the header
  const renderSortIndicator = () => {
    if (!currentSortDirection) return null;
    return currentSortDirection === "ASC" ? (
      <ArrowUpIcon className="w-3.5 h-3.5 text-blue-500 shrink-0 ml-1" />
    ) : (
      <ArrowDownIcon className="w-3.5 h-3.5 text-blue-500 shrink-0 ml-1" />
    );
  };

  // Header content — clickable for sorting
  const headerContent = (
    <div
      className="group flex items-center flex-1 min-w-0 h-full px-2 rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={() => {
        if (showSorting && onSortField) {
          onSortField();
        } else if (onSelectColumn) {
          onSelectColumn();
        }
      }}
    >
      <div className="flex items-center flex-1 min-w-0">
        {renderFieldIconComponent()}
        <span className="text-xs font-medium text-gray-700 group-hover:text-gray-900 leading-4 uppercase truncate flex-1 min-w-0 transition-colors">
          {children}
        </span>
        {renderSortIndicator()}
      </div>
    </div>
  );

  if (!showDropdown || !iconChevronDown) {
    return <div className="flex items-center justify-between w-full h-full">{headerContent}</div>;
  }

  const renderMoveLeft = () => {
    if (!isSettings) return null;
    if (headerIndex! > 0) {
      return (
        <button
          type="button"
          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
          onClick={onMoveLeft}
        >
          <ArrowLeftIcon className="w-4 h-4 text-gray-500 shrink-0" />
          <span className="text-sm font-medium text-gray-900 leading-5">Move Left</span>
        </button>
      );
    }
  };

  const renderMoveRight = () => {
    if (!isSettings) return null;
    if (headerIndex! < headerCount! - 1) {
      return (
        <button
          type="button"
          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
          onClick={onMoveRight}
        >
          <ArrowRightIcon className="w-4 h-4 text-gray-500 shrink-0" />
          <span className="text-sm font-medium text-gray-900 leading-5">Move Right</span>
        </button>
      );
    }
  };

  const renderSorting = () => {
    const sortIcon =
      sortDirection == "ASC" ? (
        <BarsArrowDownIcon className="w-4 h-4 text-gray-500 shrink-0" />
      ) : (
        <BarsArrowUpIcon className="w-4 h-4 text-gray-500 shrink-0" />
      );
    if (showSorting) {
      return (
        <button
          type="button"
          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
          onClick={onSortField}
        >
          {sortIcon}
          <span className="text-sm font-medium text-gray-900 leading-5">
            {sortDirection == "ASC" ? "Sort Ascending" : "Sort Descending"}
          </span>
        </button>
      );
    }
  };

  const renderHideColumn = () => {
    if (!isSettings) return null;
    if (headerData.selector != "name") {
      return (
        <button
          type="button"
          className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
          onClick={onHideColumn}
        >
          <EyeSlashIcon className="w-4 h-4 text-gray-500 shrink-0" />
          <span className="text-sm font-medium text-gray-900 leading-5">Hide Column</span>
        </button>
      );
    }
  };

  return (
    <div className="flex items-center justify-between w-full h-full">
      {headerContent}
      <Popover>
        <PopoverTrigger asChild>
          <div className="shrink-0 ml-1 text-gray-400 hover:text-gray-700 transition-colors cursor-pointer opacity-0 group-hover/th:opacity-100 p-0.5 rounded hover:bg-gray-200">
            <ChevronDown />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="p-1 bg-white border border-gray-200 shadow-lg z-50 min-w-[217px] rounded-md"
          align="start"
          sideOffset={4}
        >
          <div className="min-w-[217px]">
            {renderSorting()}
            {renderHideColumn()}
            {(renderMoveLeft() || renderMoveRight()) && (
              <div className="border-t border-gray-100 my-1" />
            )}
            {renderMoveLeft()}
            {renderMoveRight()}
            {isCustomField && (
              <>
                <div className="border-t border-gray-100 my-1" />
                <button
                  type="button"
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={onEditColumn}
                >
                  <Cog8ToothIcon className="w-4 h-4 text-gray-500 shrink-0" />
                  <span className="text-sm font-medium text-gray-900 leading-5">Edit</span>
                </button>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default HeadCellWrapper;
